import { useEffect, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

const stripePublishableKey =
  import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ??
  import.meta.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;

// Load Stripe lazily, only when a checkout is actually shown, and memoize it.
// Stripe.js init can fail in some browsers (e.g. Safari privacy settings,
// blocked script); on failure we drop the cached promise so a retry can work.
let stripePromise: Promise<Stripe | null> | null = null;

function getStripe(): Promise<Stripe | null> {
  if (stripePromise) return stripePromise;
  const promise = loadStripe(stripePublishableKey).catch((err) => {
    stripePromise = null;
    throw err;
  });
  stripePromise = promise;
  return promise;
}

interface EmbeddedStripeCheckoutProps {
  clientSecret: string;
  onComplete?: (sessionId?: string) => void; // optional callback
  onCancel?: () => void; // optional callback
}

export default function EmbeddedStripeCheckout({
  clientSecret,
  onComplete,
  onCancel,
}: EmbeddedStripeCheckoutProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!stripePublishableKey) return;
    let cancelled = false;
    setError(null);
    getStripe()
      .then((instance) => {
        if (cancelled) return;
        if (instance) setStripe(instance);
        else setError("Unable to load the payment form.");
      })
      .catch((err) => {
        console.error("Stripe.js failed to load", err);
        if (!cancelled) setError("Unable to load the payment form.");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // Optional: listen for completion via postMessage or polling
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Stripe can send messages — you can extend later
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  if (!stripePublishableKey) {
    return (
      <div className="text-red-500">
        Missing Stripe publishable key configuration
      </div>
    );
  }

  if (!clientSecret) {
    return <div className="text-red-500">Missing payment configuration</div>;
  }

  if (error) {
    return (
      <div className="w-full rounded-lg border border-gray-200 bg-white p-4 text-center">
        <div className="mb-4 text-red-600">{error}</div>
        <button
          type="button"
          className="underline"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stripe) {
    return <div className="min-h-[500px] w-full" aria-busy="true" />;
  }

  return (
    <div className="min-h-[500px] w-full rounded-lg border border-gray-200 bg-white p-4">
      <EmbeddedCheckoutProvider
        stripe={stripe}
        options={{
          clientSecret, // ← the magic value
          onComplete: () => {
            // Called when customer completes (success OR cancel)
            // You can poll /session-status or redirect to your return page
            console.log("Checkout completed!");
            onComplete?.();
          },
        }}
      >
        <EmbeddedCheckout className="w-full" />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
